import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import type { SpaceConflictTableRow, ResolutionType } from '@/types/space-conflicts'
import {
  RESOLUTION_TYPES,
  RESOLUTION_TYPE_LABELS,
  CONFLICT_SEVERITY_LABELS,
  CONFLICT_SEVERITY_VARIANTS,
} from '@/types/space-conflicts'

interface ConflictResolutionDialogProps {
  conflict: SpaceConflictTableRow | null
  isOpen: boolean
  isSubmitting: boolean
  onClose: () => void
  onSubmit: (resolutionType: ResolutionType | null, notes: string) => void
}

export function ConflictResolutionDialog({
  conflict,
  isOpen,
  isSubmitting,
  onClose,
  onSubmit,
}: ConflictResolutionDialogProps) {
  const [resolutionType, setResolutionType] = useState<ResolutionType | null>(null)
  const [notes, setNotes] = useState('')

  const handleSubmit = () => {
    onSubmit(resolutionType, notes)
    // Reset form
    setResolutionType(null)
    setNotes('')
  }

  const handleClose = () => {
    // Reset form
    setResolutionType(null)
    setNotes('')
    onClose()
  }

  if (!conflict) return null

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Resolve Space Conflict</DialogTitle>
          <DialogDescription>
            Provide resolution details for this space conflict
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Conflict Summary */}
          <div className="space-y-3 rounded-lg bg-muted/50 p-4">
            <div className="text-sm font-medium">Conflict Summary</div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-muted-foreground">Unit</div>
                <div className="font-medium">{conflict.unit_code || '-'}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Location</div>
                <div className="font-medium">{conflict.location_name || '-'}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Severity</div>
                <Badge variant={CONFLICT_SEVERITY_VARIANTS[conflict.severity]}>
                  {CONFLICT_SEVERITY_LABELS[conflict.severity]}
                </Badge>
              </div>
              <div>
                <div className="text-muted-foreground">Shortfall</div>
                <div className="font-medium">
                  {conflict.shortfall_sqft ? `${conflict.shortfall_sqft} sq ft` : '-'}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground">Conflict Date</div>
                <div className="font-medium">{conflict.conflict_date}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Affected Transients</div>
                <div className="font-medium">
                  {conflict.affected_transient_ids?.length ?? 0}
                </div>
              </div>
            </div>

            {/* Affected Transient IDs */}
            {conflict.affected_transient_ids && conflict.affected_transient_ids.length > 0 && (
              <div className="space-y-2 border-t border-border pt-3">
                <div className="text-xs font-medium text-muted-foreground">
                  Affected Transient Stays
                </div>
                <div className="flex flex-wrap gap-2">
                  {conflict.affected_transient_ids.map((id) => (
                    <Badge key={id} variant="outline" className="font-mono text-xs">
                      {id.slice(0, 8)}...
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Resolution Form */}
          <div className="space-y-4">
            {/* Resolution Type */}
            <div className="space-y-2">
              <Label htmlFor="resolution-type">Resolution Type *</Label>
              <Select value={resolutionType || ''} onValueChange={(v) => setResolutionType(v as ResolutionType)}>
                <SelectTrigger id="resolution-type">
                  <SelectValue placeholder="Select resolution type..." />
                </SelectTrigger>
                <SelectContent>
                  {RESOLUTION_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {RESOLUTION_TYPE_LABELS[type]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Resolution Notes */}
            <div className="space-y-2">
              <Label htmlFor="resolution-notes">Resolution Notes</Label>
              <Textarea
                id="resolution-notes"
                placeholder="Describe how this conflict was resolved..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!resolutionType || isSubmitting}
            loading={isSubmitting}
          >
            {isSubmitting ? 'Resolving...' : 'Resolve Conflict'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
